import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { ApolloProvider, type ApolloClient, type NormalizedCacheObject } from '@apollo/client';
import { Spinner } from '@semsis/ui/Spinner';
import { ToastProvider } from '@semsis/ui/Toast';
import { ThemeProvider } from '@semsis/ui/theme';
import { OrganizationProvider } from '@referee/graphql-client';
import { ErrorBoundary } from '@referee/error-handling';
import { TrainingModeProvider } from './context/TrainingModeContext';
import { TrainingLayout } from './layouts/TrainingLayout';

const CatalogPicker = lazy(() => import('./pages/CatalogPicker').then((m) => ({ default: m.CatalogPicker })));
const SetupScreen = lazy(() => import('./pages/SetupScreen').then((m) => ({ default: m.SetupScreen })));
const ExamRun = lazy(() => import('./pages/ExamRun').then((m) => ({ default: m.ExamRun })));
const ResultsScreen = lazy(() => import('./pages/ResultsScreen').then((m) => ({ default: m.ResultsScreen })));

interface AppProps {
  apolloClient: ApolloClient<NormalizedCacheObject>;
}

/**
 * The four-route table (TRAIN-01/02/03/04): every screen renders inside the shared
 * branded layout via its <Outlet />, and an unmatched path redirects to the picker
 * rather than a blank page. Exported separately from the provider stack below so the
 * route table is testable inside a plain MemoryRouter without touching window.history.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route element={<TrainingLayout />}>
        <Route index element={<CatalogPicker />} />
        <Route path=":catalogId" element={<SetupScreen />} />
        <Route path=":catalogId/exam" element={<ExamRun />} />
        <Route path=":catalogId/results" element={<ResultsScreen />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Deliberately no router path-prefix option below (unlike the sibling anonymous test
// frontend's path-prefixed deploy): this app is served at the federation subdomain root,
// so adding one would break every route.
export function App({ apolloClient }: AppProps) {
  return (
    <ThemeProvider>
      <ErrorBoundary>
        <ToastProvider>
          <ApolloProvider client={apolloClient}>
            <OrganizationProvider>
              <BrowserRouter>
                <TrainingModeProvider><Suspense fallback={<Spinner page />}><AppRoutes /></Suspense></TrainingModeProvider>
              </BrowserRouter>
            </OrganizationProvider>
          </ApolloProvider>
        </ToastProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}
